/**
 * Progression State Service
 * Manages Intel/XP system with Dexie + Firebase sync (replacing localStorage)
 */

import { db, type DBProgressionState, type Pillar } from './db';
import { CURRENT_OPERATION } from './xpSystem';

export function getWeekKey(d: Date = new Date()): string {
  // ISO week key: YYYY-Www
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  // Thursday in current week decides the year.
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

export function getDefaultProgressionState(userId: string): DBProgressionState {
  const weekKey = getWeekKey();
  return {
    userId,
    version: 1,
    level: 1,
    levelIntel: 0,
    lifetimeIntel: 0,
    weeklyIntel: 0,
    weekKey,
    awardedSessionIds: [],
    pillarIntel: {
      strength: 0,
      endurance: 0,
      bodyweight: 0,
      recovery: 0,
      consistency: 0,
    },
    operation: {
      ...CURRENT_OPERATION,
      intelEarned: 0,
    },
    rank: {
      current: 'E-1',
      title: 'Private',
      intel: 0,
      prestigeLevel: 0,
    },
    unlockedCallingCards: ['default'],
    unlockedEmblems: ['default'],
    unlockedTitles: ['Recruit'],
    unlockedAvatars: ['default'],
    equippedCallingCard: 'default',
    equippedEmblem: 'default',
    equippedTitle: 'Recruit',
    equippedAvatar: 'default',
    // Streak & Daily Tracking
    streakDays: 0,
    longestStreak: 0,
    streakFreezeUsedThisMonth: false,
    milestonesUnlocked: [],

    unlockedArchetypes: [],
    equippedArchetype: null,
    archetypeProgress: {
      'pack-mule': {
        ruckMiles: 0,
        ruckSessions: 0,
        longestRuckWith50lbs: 0,
        enduranceIntel: 0,
        operatorScore: 0,
        unlocked: false,
      },
      'breacher': {
        lifetimeVolume: 0,
        oneRMTotal: 0,
        strengthSessions: 0,
        strengthIntel: 0,
        operatorScore: 0,
        unlocked: false,
      },
      'scout': {
        runMiles: 0,
        cardioSessions: 0,
        fastestMile: 999999, // Very high default (no mile time yet)
        enduranceIntel: 0,
        operatorScore: 0,
        unlocked: false,
      },
      'assaulter': {
        totalPullUps: 0,
        totalPushUps: 0,
        bodyweightSessions: 0,
        maxMuscleUpsInSession: 0,
        bodyweightIntel: 0,
        operatorScore: 0,
        unlocked: false,
      },
      'operator': {
        minPillarIntel: 0,
        totalSessions: 0,
        programsCompleted: 0,
        operatorScore: 0,
        avgReadiness30d: 0,
        readinessDaysAbove70: 0,
        unlocked: false,
      },
    },
    // Weekly Mission Completions
    missionCompletions: {
      weekKey,
      completedMissionIds: [],
      intelEarnedFromMissions: 0,
    },
    syncStatus: 'local',
    updatedAt: new Date(),
  };
}

/**
 * Load progression state from Dexie
 */
export async function loadProgressionState(userId: string): Promise<DBProgressionState> {
  try {
    let state = await db.progressionStates.get(userId);
    
    if (!state) {
      console.log('� No progression state found, creating default');
      const defaultState = getDefaultProgressionState(userId);
      await db.progressionStates.put(defaultState);
      return defaultState;
    }

    // Migration: Add new fields if they don't exist
    let needsUpdate = false;
    
    if (!state.rank) {
      console.log('� Migrating: Adding rank system fields');
      state.rank = {
        current: 'E-1',
        title: 'Private',
        intel: state.lifetimeIntel || 0,
        prestigeLevel: 0,
      };
      needsUpdate = true;
    }
    
    if (!state.unlockedCallingCards) {
      console.log('� Migrating: Adding cosmetic unlock fields');
      state.unlockedCallingCards = ['default'];
      state.unlockedEmblems = ['default'];
      state.unlockedTitles = ['Recruit'];
      state.unlockedAvatars = ['default'];
      state.equippedCallingCard = 'default';
      state.equippedEmblem = 'default';
      state.equippedTitle = 'Recruit';
      state.equippedAvatar = 'default';
      needsUpdate = true;
    }
    
    // Migration: Add streak and milestone fields
    if (state.streakDays === undefined) {
      console.log('� Migrating: Adding streak and milestone fields');
      state.streakDays = 0;
      state.longestStreak = 0;
      state.streakFreezeUsedThisMonth = false;
      state.milestonesUnlocked = [];
      needsUpdate = true;
    }

    // Monthly reset for streak freeze
    const currentMonthKey = new Date().toISOString().slice(0, 7); // YYYY-MM
    if (state.streakFreezeMonthKey !== currentMonthKey) {
      state.streakFreezeMonthKey = currentMonthKey;
      state.streakFreezeUsedThisMonth = false;
      needsUpdate = true;
    }

    if (!state.archetypeProgress) {
      console.log('� Migrating: Adding archetype progress tracking');
      state.unlockedArchetypes = [];
      state.equippedArchetype = null;
      state.archetypeProgress = {
        'pack-mule': {
          ruckMiles: 0,
          ruckSessions: 0,
          longestRuckWith50lbs: 0,
          enduranceIntel: state.pillarIntel?.endurance || 0,
          operatorScore: 0,
          unlocked: false,
        },
        'breacher': {
          lifetimeVolume: 0,
          oneRMTotal: 0,
          strengthSessions: 0,
          strengthIntel: state.pillarIntel?.strength || 0,
          operatorScore: 0,
          unlocked: false,
        },
        'scout': {
          runMiles: 0,
          cardioSessions: 0,
          fastestMile: 999999,
          enduranceIntel: state.pillarIntel?.endurance || 0,
          operatorScore: 0,
          unlocked: false,
        },
        'assaulter': {
          totalPullUps: 0,
          totalPushUps: 0,
          bodyweightSessions: 0,
          maxMuscleUpsInSession: 0,
          bodyweightIntel: state.pillarIntel?.bodyweight || 0,
          operatorScore: 0,
          unlocked: false,
        },
        'operator': {
          minPillarIntel: 0,
          totalSessions: 0,
          programsCompleted: 0,
          operatorScore: 0,
          avgReadiness30d: 0,
          readinessDaysAbove70: 0,
          unlocked: false,
        },
      };
      needsUpdate = true;
    }

    // Migration: Add mission completions tracking
    if (!state.missionCompletions) {
      console.log('� Migrating: Adding mission completions tracking');
      state.missionCompletions = {
        weekKey: state.weekKey || getWeekKey(),
        completedMissionIds: [],
        intelEarnedFromMissions: 0,
      };
      needsUpdate = true;
    }

    /**
     * Migration: retroactively backfill programsCompleted for users who
     * already finished a program before this counter existed.
     *
     * GATED ON A PERSISTED MARKER, not on the counter's value. The guard used
     * to be `programsCompleted === 0` — a condition that is permanently true
     * for everyone who has not yet finished a program, so this "one-time"
     * migration re-ran on EVERY call to loadProgressionState. Each run does a
     * program-selection lookup that falls through to a live Firestore getDoc
     * when Dexie is cold.
     *
     * That was most of a measured 341 program-selection reads from a single
     * visit to the Barracks page (the Intel backfill calls this ~2x per
     * historical session). Billable traffic for a migration that had already
     * decided it had nothing to do.
     */
    const migrations = (state as { migrations?: Record<string, boolean> }).migrations ?? {};
    if (!migrations.programsCompletedBackfill) {
      try {
        if (state.archetypeProgress?.['operator']?.programsCompleted === 0) {
          const { getUserProgramSelection } = await import('./programManagement');
          const { getCachedProgram } = await import('./programCache');
          const selection = await getUserProgramSelection(userId);
          if (selection?.programId) {
            const program = await getCachedProgram(selection.programId, userId);
            const duration = program?.metadata?.duration || 13;
            if (
              selection.currentWeek > duration ||
              (selection.currentWeek === duration && selection.currentDay === 7)
            ) {
              state.archetypeProgress['operator'].programsCompleted = 1;
              console.log(' Migrating: Retroactively set programsCompleted=1 (user already completed program)');
            }
          }
        }
        // Mark done either way — "checked and there was nothing to do" is a
        // completed migration. This is what stops the re-run.
        migrations.programsCompletedBackfill = true;
        (state as { migrations?: Record<string, boolean> }).migrations = migrations;
        needsUpdate = true;
      } catch (e) {
        // Non-critical: leave the marker unset so it retries on a later load.
        console.warn(' programsCompleted backfill skipped:', e);
      }
    }

    // Weekly reset check
    const currentWeekKey = getWeekKey();
    if (state.weekKey !== currentWeekKey) {
      console.log(` Week changed: ${state.weekKey} → ${currentWeekKey}, resetting weekly intel`);
      state.weekKey = currentWeekKey;
      state.weeklyIntel = 0;
      // Reset mission completions for new week
      state.missionCompletions = {
        weekKey: currentWeekKey,
        completedMissionIds: [],
        intelEarnedFromMissions: 0,
      };
      needsUpdate = true;
    }

    // Operation rollover check
    if (state.operation?.id !== CURRENT_OPERATION.id) {
      console.log(` Operation changed: ${state.operation?.id} → ${CURRENT_OPERATION.id}`);
      state.operation = { ...CURRENT_OPERATION, intelEarned: 0 };
      needsUpdate = true;
    }

    if (needsUpdate) {
      state.syncStatus = 'pending';
      state.updatedAt = new Date();
      await db.progressionStates.put(state);
    }

    return state;
  } catch (error) {
    console.error(' Failed to load progression state:', error);
    return getDefaultProgressionState(userId);
  }
}

/**
 * Save progression state to Dexie
 */
export async function saveProgressionState(state: DBProgressionState): Promise<void> {
  try {
    state.syncStatus = 'pending';
    state.updatedAt = new Date();
    await db.progressionStates.put(state);
    console.log(' Progression state saved to Dexie');
  } catch (error) {
    console.error(' Failed to save progression state:', error);
    throw error;
  }
}

/**
 * Mark progression state as synced
 */
export async function markProgressionSynced(userId: string): Promise<void> {
  try {
    const state = await db.progressionStates.get(userId);
    if (state) {
      state.syncStatus = 'synced';
      state.syncedAt = new Date();
      await db.progressionStates.put(state);
    }
  } catch (error) {
    console.error(' Failed to mark progression as synced:', error);
  }
}

/**
 * Equip an archetype (must be unlocked)
 * @returns true if equipped successfully, false if not unlocked
 */
export async function equipArchetype(userId: string, archetypeId: string | null): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    // Allow unequipping (setting to null)
    if (archetypeId === null) {
      state.equippedArchetype = null;
      await saveProgressionState(state);
      console.log('� Archetype unequipped');
      return true;
    }

    // Check if archetype is unlocked
    const progress = state.archetypeProgress[archetypeId as keyof typeof state.archetypeProgress];
    if (!progress?.unlocked) {
      console.warn(` Cannot equip "${archetypeId}" - not unlocked`);
      return false;
    }

    state.equippedArchetype = archetypeId;
    await saveProgressionState(state);
    console.log(` Archetype equipped: ${archetypeId}`);
    return true;
  } catch (error) {
    console.error(' Failed to equip archetype:', error);
    return false;
  }
}

/**
 * Equip a title (must be unlocked)
 */
export async function equipTitle(userId: string, titleId: string): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    if (!state.unlockedTitles?.includes(titleId)) {
      console.warn(` Cannot equip title "${titleId}" - not unlocked`);
      return false;
    }

    state.equippedTitle = titleId;
    await saveProgressionState(state);
    console.log(` Title equipped: ${titleId}`);
    return true;
  } catch (error) {
    console.error(' Failed to equip title:', error);
    return false;
  }
}

/**
 * Equip a calling card (must be unlocked)
 */
export async function equipCallingCard(userId: string, cardId: string): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    if (!state.unlockedCallingCards?.includes(cardId)) {
      console.warn(` Cannot equip calling card "${cardId}" - not unlocked`);
      return false;
    }

    state.equippedCallingCard = cardId;
    await saveProgressionState(state);
    console.log(` Calling card equipped: ${cardId}`);
    return true;
  } catch (error) {
    console.error(' Failed to equip calling card:', error);
    return false;
  }
}

/**
 * Equip an emblem (must be unlocked)
 */
export async function equipEmblem(userId: string, emblemId: string): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    if (!state.unlockedEmblems?.includes(emblemId)) {
      console.warn(` Cannot equip emblem "${emblemId}" - not unlocked`);
      return false;
    }

    state.equippedEmblem = emblemId;
    await saveProgressionState(state);
    console.log(` Emblem equipped: ${emblemId}`);
    return true;
  } catch (error) {
    console.error(' Failed to equip emblem:', error);
    return false;
  }
}

/**
 * Equip an avatar (must be unlocked)
 */
export async function equipAvatar(userId: string, avatarId: string): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    if (!state.unlockedAvatars?.includes(avatarId)) {
      console.warn(` Cannot equip avatar "${avatarId}" - not unlocked`);
      return false;
    }

    state.equippedAvatar = avatarId;
    await saveProgressionState(state);
    console.log(` Avatar equipped: ${avatarId}`);
    return true;
  } catch (error) {
    console.error(' Failed to equip avatar:', error);
    return false;
  }
}

/**
 * Unlock a cosmetic item (title, calling card, emblem, or avatar)
 */
export async function unlockCosmetic(
  userId: string,
  type: 'title' | 'callingCard' | 'emblem' | 'avatar',
  itemId: string
): Promise<boolean> {
  try {
    const state = await loadProgressionState(userId);

    switch (type) {
      case 'title':
        if (!state.unlockedTitles) state.unlockedTitles = ['Recruit'];
        if (!state.unlockedTitles.includes(itemId)) {
          state.unlockedTitles.push(itemId);
          console.log(` Title unlocked: ${itemId}`);
        }
        break;
      case 'callingCard':
        if (!state.unlockedCallingCards) state.unlockedCallingCards = ['default'];
        if (!state.unlockedCallingCards.includes(itemId)) {
          state.unlockedCallingCards.push(itemId);
          console.log(` Calling card unlocked: ${itemId}`);
        }
        break;
      case 'emblem':
        if (!state.unlockedEmblems) state.unlockedEmblems = ['default'];
        if (!state.unlockedEmblems.includes(itemId)) {
          state.unlockedEmblems.push(itemId);
          console.log(` Emblem unlocked: ${itemId}`);
        }
        break;
      case 'avatar':
        if (!state.unlockedAvatars) state.unlockedAvatars = ['default'];
        if (!state.unlockedAvatars.includes(itemId)) {
          state.unlockedAvatars.push(itemId);
          console.log(` Avatar unlocked: ${itemId}`);
        }
        break;
    }

    await saveProgressionState(state);
    return true;
  } catch (error) {
    console.error(` Failed to unlock ${type}:`, error);
    return false;
  }
}

/**
 * Complete a mission and award Intel
 * Returns the Intel earned, or 0 if already completed
 */
export async function completeMission(
  userId: string,
  missionId: string,
  intelReward: number,
  pillar: 'strength' | 'endurance' | 'bodyweight' | 'recovery' | 'consistency'
): Promise<{ success: boolean; intelEarned: number; message: string }> {
  try {
    const state = await loadProgressionState(userId);
    const currentWeekKey = getWeekKey();

    // Ensure mission completions are for current week
    if (!state.missionCompletions || state.missionCompletions.weekKey !== currentWeekKey) {
      state.missionCompletions = {
        weekKey: currentWeekKey,
        completedMissionIds: [],
        intelEarnedFromMissions: 0,
      };
    }

    // Check if already completed
    if (state.missionCompletions.completedMissionIds.includes(missionId)) {
      return {
        success: false,
        intelEarned: 0,
        message: 'Mission already completed this week',
      };
    }

    // Mark mission as completed
    state.missionCompletions.completedMissionIds.push(missionId);
    state.missionCompletions.intelEarnedFromMissions += intelReward;

    // Award Intel to relevant pillar
    state.pillarIntel[pillar] = (state.pillarIntel[pillar] || 0) + intelReward;

    // Add to totals
    state.levelIntel += intelReward;
    state.lifetimeIntel += intelReward;
    state.weeklyIntel += intelReward;

    // Update operation Intel
    if (state.operation) {
      state.operation.intelEarned += intelReward;
    }

    // Check for level up
    const { intelForLevel } = await import('./xpSystem');
    while (state.levelIntel >= intelForLevel(state.level)) {
      state.levelIntel -= intelForLevel(state.level);
      state.level += 1;
      console.log(` Level Up! Now level ${state.level}`);
    }

    await saveProgressionState(state);

    console.log(` Mission completed: ${missionId} | +${intelReward} Intel (${pillar})`);
    return {
      success: true,
      intelEarned: intelReward,
      message: `+${intelReward} Intel earned!`,
    };
  } catch (error) {
    console.error(' Failed to complete mission:', error);
    return {
      success: false,
      intelEarned: 0,
      message: 'Failed to complete mission',
    };
  }
}

/**
 * Get completed mission IDs for current week
 */
export async function getCompletedMissionIds(userId: string): Promise<string[]> {
  try {
    const state = await loadProgressionState(userId);
    const currentWeekKey = getWeekKey();

    if (!state.missionCompletions || state.missionCompletions.weekKey !== currentWeekKey) {
      return [];
    }

    return state.missionCompletions.completedMissionIds;
  } catch (error) {
    console.error(' Failed to get completed missions:', error);
    return [];
  }
}
